import React from 'react';
import { plural, t } from '@lingui/core/macro';

import {
  SERVICE_TYPE_CHOICES,
  ServiceType,
} from '@rover/constants/src/translations/service.constants';
import DropdownSmall from '@rover/icons/svg/dropdown-small.svg';
import { Box, Flex, Text } from '@rover/kibble/core';
import ServiceIcon from '@rover/shared/js/search/components/ServiceIcon';
import type { SearchFilters } from '@rover/types/src/SearchFilters';
import { ServiceFrequencyEnum } from '@rover/types/src/ServiceFrequency';
import type { ServiceTypeOption } from '@rover/types/src/ServiceType';

import AllFiltersButton from './AllFiltersButton';
import { formatDateRange } from './utils';

export type FilterType = 'main' | 'all';

export type Props = {
  searchFilters: SearchFilters;
  serviceType: ServiceTypeOption | null;
  language: string;
  onFilterClick: (filterType: FilterType) => void;
  advancedFiltersCount?: number;
};

// Component to render truncated text with ellipsis
const TruncatedText = ({ children, ...props }: React.ComponentProps<typeof Text>): JSX.Element => {
  return (
    <Text
      size="100"
      textColor="primary"
      sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
      {...props}
    >
      {children}
    </Text>
  );
};

// Dot component for separator
const Dot = (): JSX.Element => (
  <Text textColor="primary" size="100" sx={{ flexShrink: 0, fontFamily: 'Helvetica Neue' }}>
    •
  </Text>
);

const StickyFiltersBar = ({
  searchFilters,
  serviceType,
  language,
  onFilterClick,
  advancedFiltersCount,
}: Props): JSX.Element => {
  const { location = '', startDate, endDate, frequency, selectedDays = [] } = searchFilters;
  const { dogCount = 0, catCount = 0, puppyCount = 0 } = searchFilters;

  const serviceTypeValue = serviceType?.title || '';
  const serviceTypeSlug = serviceType?.value;

  const dateValue = formatDateRange(startDate, endDate, language);
  const petCount = dogCount + catCount + puppyCount;
  const petValue = plural(petCount, { one: `${petCount} pet`, other: `${petCount} pets` });

  const isRepeatWeekly = frequency === ServiceFrequencyEnum.REPEAT;
  const isOvernightService =
    serviceType?.value === ServiceType.OVERNIGHT_BOARDING ||
    serviceType?.value === ServiceType.OVERNIGHT_TRAVELING;

  const recurringDaysCount = selectedDays.length;
  const recurringDaysValue = plural(recurringDaysCount, {
    one: `${recurringDaysCount} day`,
    other: `${recurringDaysCount} days`,
  });

  return (
    <Box
      display={['block', 'none']}
      position="sticky"
      top="0"
      zIndex="100"
      p="4x"
      background="primary"
      variant="elevation_low"
    >
      <Flex gap="2x" alignItems="center">
        <Flex
          flex="1"
          webAs="button"
          onClick={() => onFilterClick('main')}
          background="primary"
          alignItems="center"
          padding="3x"
          border="2px solid"
          borderColor="border.primary"
          borderRadius="16px"
          height="60px"
        >
          <Box flexShrink="0" mr="3x">
            <ServiceIcon serviceType={serviceTypeSlug || SERVICE_TYPE_CHOICES.boarding} />
          </Box>
          <Flex flexDirection="column" width="100%">
            <Flex gap="1x" alignItems="center" flex="1">
              <Text
                size="100"
                fontWeight="semibold"
                textColor="primary"
                textDecoration="underline"
                sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                {serviceTypeValue || t`Boarding`}
              </Text>
              <Dot />
              <TruncatedText textColor={location ? 'primary' : 'placeholder'}>
                {location || t`Add address`}
              </TruncatedText>
            </Flex>

            <Flex gap="1x" alignItems="center" width="100%">
              {!isOvernightService && recurringDaysCount > 0 && isRepeatWeekly ? (
                <TruncatedText textColor="primary">{recurringDaysValue}</TruncatedText>
              ) : (
                <TruncatedText textColor={dateValue ? 'primary' : 'placeholder'}>
                  {dateValue || t`Add dates`}
                </TruncatedText>
              )}
              <Dot />
              <TruncatedText textColor={petCount > 0 ? 'primary' : 'placeholder'}>
                {petCount > 0 ? petValue : t`Add pets`}
              </TruncatedText>
            </Flex>
          </Flex>
          <Box m="2x" height="24px" width="24px">
            <DropdownSmall height="24px" width="24px" />
          </Box>
        </Flex>
        <AllFiltersButton onClick={() => onFilterClick('all')} badgeCount={advancedFiltersCount} />
      </Flex>
    </Box>
  );
};

export default StickyFiltersBar;
